'use client';

import Link from 'next/link';
import { useState } from 'react';
import { isValidWorkEmail } from '@/lib/demoSessionClient';

type Props = {
  onSubmit: (data: { email: string; name?: string }) => void;
  onSkip?: () => void;
};

/** Required email gate before Field engagement plan PDF download. */
export default function DemoLeadCaptureModal({ onSubmit, onSkip }: Props) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !isValidWorkEmail(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    onSubmit({ email: trimmed, name: name.trim() || undefined });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-lead-title"
        className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-800 p-5 shadow-2xl sm:p-6"
      >
        <h2 id="demo-lead-title" className="text-lg font-semibold text-[#59B9C1]">
          Email your Field engagement plan
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-300">
          Leave a work email so we know who downloaded the plan. We will not spam you.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div>
            <label htmlFor="demo-lead-email" className="mb-1 block text-xs font-medium text-gray-400">
              Work email <span className="text-red-400">*</span>
            </label>
            <input
              id="demo-lead-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              required
              className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-[#59B9C1] focus:outline-none focus:ring-1 focus:ring-[#59B9C1]"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label htmlFor="demo-lead-name" className="mb-1 block text-xs font-medium text-gray-400">
              First name (optional)
            </label>
            <input
              id="demo-lead-name"
              type="text"
              autoComplete="given-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-[#59B9C1] focus:outline-none focus:ring-1 focus:ring-[#59B9C1]"
              placeholder="Alex"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <p className="text-xs text-gray-500">
            See our{' '}
            <Link href="/data-privacy" className="text-[#59B9C1] hover:underline">
              privacy policy
            </Link>
            .
          </p>

          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end sm:gap-3">
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={!email.trim()}
              className="rounded-md bg-[#59B9C1] px-4 py-2 text-sm font-medium text-white hover:bg-[#4aa7ae] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Download PDF
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
